import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
// We JUST launched, so we don't have real reviews yet. Rather than fake social
// proof, this section is honest and self-aware: these are the (clearly
// labelled) reviews we're *hoping* to get — placeholder quotes attributed to
// "you, probably" and friends, with a playful "Reserved" tag so it's obvious
// the wall is waiting to be filled in. It earns trust by being in on the joke.
type PlaceholderReview = {
  name: string;
  handle: string;
  text: string;
  bg: string;
  initials: string;
};
const PLACEHOLDERS: PlaceholderReview[] = [{
  name: 'You, probably',
  handle: 'in about three weeks',
  initials: 'YOU',
  bg: '#EA580C',
  text: "Okay this is unreasonably good. I @mentioned Giga once and it cleared half my to-do list before I'd finished my coffee."
}, {
  name: 'Your Head of Ops',
  handle: 'forwarding this to the team',
  initials: 'OPS',
  bg: '#2B211A',
  text: 'I have not opened a single status-update thread this week. I do not know who I am anymore. Thank you, Giga.'
}, {
  name: 'That one skeptical PM',
  handle: 'who said it would never work',
  initials: 'PM',
  bg: '#9C8E82',
  text: "Fine. FINE. It's good. I was wrong. Please stop screenshotting my earlier messages."
}, {
  name: 'A future power user',
  handle: 'currently #131 in line',
  initials: 'FAN',
  bg: '#EA580C',
  text: 'Would write a glowing review but Giga already drafted one for me and honestly it slaps. Approving as-is.'
}, {
  name: 'Someone on the waitlist',
  handle: 'refreshing their inbox',
  initials: 'WL',
  bg: '#2B211A',
  text: 'Cannot review yet — still waiting to get in. This is my review of the waitlist. The waitlist is very good. Let me in.'
}];
// Tongue-in-cheek "press" blurbs — the same gag the user runs on their other
// app: obviously-not-real spins on real outlet names ("Totally Verge",
// "Definitely TechCrunch"…) so it's unmistakably a joke, not a real press
// quote. Recognizable logos via simpleicons with a monogram fallback.
type PressBlurb = {
  outlet: string;
  // simpleicons slug (primary logo source)
  slug: string;
  // dashboard-icons slug (full-color fallback, forced white to match)
  iconSlug: string;
  brand: string;
  mono: string;
  text: string;
};
const PRESS: PressBlurb[] = [{
  outlet: 'Totally The Verge',
  slug: 'theverge',
  iconSlug: 'the-verge',
  brand: '#5200FF',
  mono: 'V',
  text: 'Finally, an AI that does the work instead of explaining how you could do the work.'
}, {
  outlet: 'Definitely TechCrunch',
  slug: 'techcrunch',
  iconSlug: 'techcrunch',
  brand: '#0A8540',
  mono: 'TC',
  text: 'Better than your last three productivity apps combined. Possibly better than your last three hires.'
}, {
  outlet: 'Almost Wired',
  slug: 'wired',
  iconSlug: 'wired',
  brand: '#000000',
  mono: 'W',
  text: 'The second-best thing to happen to Slack since the side panel. Maybe the first.'
}, {
  outlet: 'Rolling Stonez',
  slug: 'rollingstone',
  iconSlug: 'rolling-stone',
  brand: '#C8102E',
  mono: 'RS',
  text: "Honestly, if this existed sooner, half our status meetings wouldn't have."
}];
// Resilient outlet logo: the real brand mark rendered white on a brand-colored
// circle. Tries simpleicons first, then the dashboard-icons SVG (forced to
// white so it matches), and only falls back to a monogram chip if both fail —
// so each outlet shows its actual logo wherever it's available.
function PressLogo({
  blurb
}: {
  blurb: PressBlurb;
}) {
  // 0 = simpleicons, 1 = dashboard-icons (white-filtered), 2 = monogram
  const [stage, setStage] = useState(0);
  return <span className="flex items-center justify-center w-11 h-11 rounded-full shrink-0 overflow-hidden text-white font-bold text-[11px] tracking-tight" style={{
    backgroundColor: blurb.brand
  }} aria-hidden="true" data-id="element-3145">
      {stage >= 2 ? blurb.mono : <img key={stage} src={stage === 0 ? `https://cdn.simpleicons.org/${blurb.slug}/white` : `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${blurb.iconSlug}.svg`} alt="" aria-hidden="true" onError={() => setStage(s => s + 1)} className={`w-5 h-5 object-contain ${stage === 1 ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-3146" />}
    </span>;
}
export function WallOfLove() {
  return <section className="relative w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden" data-id="element-3147">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 dot-matrix opacity-50 pointer-events-none" data-id="element-3148" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 mb-12 md:mb-16 text-center" data-id="element-3149">
        <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] max-w-3xl mx-auto" data-id="element-3150">
          Here's what we're pretty sure you'll say once you're in.
        </h2>
        <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-3151">
          We just launched, so we're still filling these spots up. Want yours
          featured? Get in and prove us right.
        </p>
      </div>

      {/* "What the press (probably) would say" — spoofed press blurbs with
                        obviously-not-real outlet names, placed up top (press first, then
                        the personal reviews below) so it reads as an honest joke rather
                        than fabricated press coverage tacked onto the end.
                        Hidden for now per user request; code preserved for later. */}
      {false && <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 mb-16 md:mb-24" data-id="element-3152">
          <div className="flex items-center justify-center mb-10 md:mb-14" data-id="element-3153">
            <h3 className="text-2xl md:text-3xl font-title font-medium text-[#2B211A] tracking-tight text-center" data-id="element-3154">
              What the press (probably) would say
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-id="element-3155">
            {PRESS.map(blurb => <figure key={blurb.outlet} className="flex flex-col items-center text-center bg-white rounded-3xl p-7 md:p-8 border border-dashed border-neutral-300 shadow-sm h-full" data-id="element-3156">
                <PressLogo blurb={blurb} data-id="element-3157" />
                <blockquote className="mt-5 text-[15px] text-neutral-600 leading-relaxed" data-id="element-3158">
                  "{blurb.text}"
                </blockquote>
                <figcaption className="mt-auto pt-6 text-sm font-bold text-[#2B211A]" data-id="element-3159">
                  {blurb.outlet}
                </figcaption>
              </figure>)}
          </div>
        </div>}

      {/* Scroller */}
      <div className="relative z-10 w-full flex overflow-x-hidden" data-id="element-3160">
        <motion.div animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: 40,
        ease: 'linear',
        repeat: Infinity
      }} className="flex gap-6 px-6 w-max" data-id="element-3161">
          {[...PLACEHOLDERS, ...PLACEHOLDERS].map((t, i) => <div key={i} className="relative w-[380px] bg-white rounded-3xl p-8 border border-dashed border-neutral-300 shadow-sm shrink-0 flex flex-col" data-id="element-3162">
              {/* Honest "this isn't real yet" tag — keeps the section playful
               and trustworthy instead of faking social proof. */}
              <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFF1E8] text-[#C2410C] text-[10px] font-bold uppercase tracking-wide" data-id="element-3163">
                <Quote size={10} strokeWidth={3} data-id="element-3164" />
                Reserved
              </span>

              <div className="flex items-center gap-4 mb-5" data-id="element-3165">
                <div className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center text-white font-bold text-xs tracking-wide" style={{
              backgroundColor: t.bg
            }} aria-hidden="true" data-id="element-3166">
                  {t.initials}
                </div>
                <div className="min-w-0" data-id="element-3167">
                  <div className="font-bold text-[#2B211A] text-base truncate" data-id="element-3168">
                    {t.name}
                  </div>
                  <div className="text-sm text-[#EA580C] truncate" data-id="element-3169">
                    {t.handle}
                  </div>
                </div>
              </div>
              <p className="text-[15px] text-neutral-600 leading-relaxed" data-id="element-3170">
                "{t.text}"
              </p>
            </div>)}
        </motion.div>
      </div>
    </section>;
}